import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash2, Pencil, X, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API } from '../../content/data';
import { PAGE_OPTIONS } from '../../components/CustomSections';
import { MediaPicker } from './design';
import { LAYERS, TRIAD, JOURNEY, SERVICES, MEMBERSHIP_ADVANTAGES, CSR_STEPS, STUDENT_BENEFITS, ALUMNI_BENEFITS, DIGITAL_ASSETS, LEARN_BLOCKS } from '../../content/data';

const inputCls = 'w-full rounded-xl border border-navy/15 bg-white px-4 py-3 text-sm text-navy outline-none transition-colors focus:border-electric placeholder:text-navy/40';
const labelCls = 'overline !text-[10px] mb-1.5 block text-navy/50';

function useAdminFetch(key, onAuthError) {
  return useCallback(async (fn) => {
    try {
      return await fn(axios.create({ headers: { 'X-Admin-Key': key } }));
    } catch (e) {
      if (e.response?.status === 401) onAuthError();
      throw e;
    }
  }, [key, onAuthError]);
}

const HOME_JOURNEY = [
  { n: '01', t: 'Learn', d: 'Develop market-relevant digital and technology capabilities.' },
  { n: '02', t: 'Build', d: 'Create live digital properties and real projects.' },
  { n: '03', t: 'Prove', d: 'Turn performance, projects and outcomes into visible evidence.' },
  { n: '04', t: 'Connect', d: 'Move towards enterprise projects, opportunities and an expanding professional ecosystem.' },
];
const HOME_ENGAGEMENT = [
  { t: 'Talent', d: 'Connect with capability developed through practical work.' },
  { t: 'Technology', d: 'Access digital, AI, cybersecurity and technical expertise.' },
  { t: 'Intelligence', d: 'Use research and ecosystem data to make better decisions.' },
  { t: 'Reach', d: 'Leverage digital marketing and the wider creator ecosystem.' },
  { t: 'Impact', d: 'Connect enterprise participation with measurable social and economic outcomes.' },
];
const ABOUT_WHY = [
  { t: 'For Young People', d1: 'A qualification alone does not always demonstrate whether someone can actually build, execute or generate value.', d2: "DITEC's learning model is designed to close that gap through practical work, digital assets, projects, measurable performance and commercial exposure." },
  { t: 'For Enterprises', d1: 'Companies need dependable technical and digital capability, but finding, evaluating and deploying that capability can be expensive and time-consuming.', d2: 'DITEC connects enterprise requirements with a developing pool of performance-backed talent and institutional capabilities.' },
  { t: 'For Institutions & Government', d1: 'There is also a need for delivery mechanisms that can turn programmes and policy objectives into measurable outcomes.', d2: 'DITEC is designed to operate at this intersection.' },
];
const WHO = [
  { t: 'After Class 12', d: 'Start developing focused digital capabilities at an early stage.' },
  { t: 'College Students', d: 'Build practical expertise alongside conventional education.' },
  { t: 'Aspiring Creators', d: 'Turn content, technology and audience-building into a structured capability.' },
  { t: 'Future Freelancers & Entrepreneurs', d: 'Develop the skills required to acquire clients, build digital products and create independent income opportunities.' },
  { t: 'Professionals', d: "Upgrade existing capabilities, develop new digital and technology competencies, and use DITEC's practical ecosystem to strengthen career growth, consulting, leadership or entrepreneurial pathways." },
];
const PILLARS = [
  { t: 'Talent Acquisition', d: 'See capability before you hire it.', to: '/enterprises/talent' },
  { t: 'CSR Routing', d: 'Make your CSR allocation work across multiple outcomes.', to: '/enterprises/csr' },
  { t: 'Marketing Cell', d: 'Digital reach backed by intelligence.', to: '/enterprises/marketing-cell' },
  { t: 'Research & Diagnostics', d: 'Understand before you invest.', to: '/enterprises/research' },
  { t: 'Enterprise Membership', d: 'One relationship. Seven strategic advantages.', to: '/enterprises/membership' },
  { t: 'Institutional Partnerships', d: 'Build a stronger digital pathway for your students.', to: '/institutions' },
];

const LIST_GROUPS = [
  { page: 'Home', lists: [
    { key: 'home.journey', label: 'Learn → Build → Prove → Connect steps', fields: [['n', '#'], ['t', 'Title'], ['d', 'Description']], defaults: HOME_JOURNEY },
    { key: 'home.engagement', label: 'Enterprise Engagement items', fields: [['t', 'Title'], ['d', 'Description']], defaults: HOME_ENGAGEMENT },
  ]},
  { page: 'About', lists: [
    { key: 'about.why', label: 'Why DITEC Exists cards', fields: [['t', 'Title'], ['d1', 'First paragraph'], ['d2', 'Second paragraph']], defaults: ABOUT_WHY },
    { key: 'about.layers', label: 'Seven Ecosystem Layers', fields: [['n', '#'], ['name', 'Name'], ['desc', 'Description']], defaults: LAYERS },
    { key: 'about.triad', label: 'Triad Database nodes', fields: [['title', 'Title'], ['desc', 'Description']], defaults: TRIAD },
    { key: 'about.journey', label: 'DITEC Journey steps', fields: [['step', 'Step'], ['desc', 'Description']], defaults: JOURNEY },
  ]},
  { page: 'Students', lists: [
    { key: 'students.who', label: 'Who Can Join cards', fields: [['t', 'Title'], ['d', 'Description']], defaults: WHO },
    { key: 'students.assets', label: 'Four Digital Assets', fields: [['title', 'Title'], ['desc', 'Description']], defaults: DIGITAL_ASSETS },
    { key: 'students.learn', label: 'What You Learn blocks', fields: [['n', '#'], ['title', 'Title'], ['desc', 'Description']], defaults: LEARN_BLOCKS },
    { key: 'students.benefits', label: 'Student Benefits', fields: [['title', 'Title'], ['desc', 'Description']], defaults: STUDENT_BENEFITS },
    { key: 'students.alumni', label: 'Alumni Benefits', fields: [['title', 'Title'], ['desc', 'Description']], defaults: ALUMNI_BENEFITS },
  ]},
  { page: 'Enterprises', lists: [
    { key: 'enterprises.pillars', label: 'Pillar cards', fields: [['t', 'Title'], ['d', 'Description'], ['to', 'Link path']], defaults: PILLARS },
    { key: 'enterprises.services', label: 'Enterprise Services stack', fields: [['title', 'Title'], ['items', 'Items (one per line)', 'lines']], defaults: SERVICES },
  ]},
  { page: 'CSR', lists: [
    { key: 'csr.steps', label: 'CSR Routing steps', fields: [['n', '#'], ['title', 'Title'], ['desc', 'Description']], defaults: CSR_STEPS },
  ]},
  { page: 'Membership', lists: [
    { key: 'membership.advantages', label: 'Seven Advantages', fields: [['n', '#'], ['title', 'Title'], ['desc', 'Description']], defaults: MEMBERSHIP_ADVANTAGES },
  ]},
];

/* ---------------- List editor ---------------- */
function ListEditor({ cfg, overrides, run, reload }) {
  const current = overrides[cfg.key] ? safeParse(overrides[cfg.key], cfg.defaults) : cfg.defaults;
  const [draft, setDraft] = useState(null);
  const items = draft ?? current;
  const dirty = draft !== null;

  const update = (i, k, v) => setDraft(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const add = () => setDraft([...items, Object.fromEntries(cfg.fields.map(([k]) => [k, k === 'items' ? [] : '']))]);
  const remove = (i) => setDraft(items.filter((_, idx) => idx !== i));

  const save = async () => {
    await run((c) => c.put(`${API}/admin/content/${encodeURIComponent(cfg.key)}`, { value: JSON.stringify(items) }));
    setDraft(null);
    reload();
  };
  const reset = async () => {
    await run((c) => c.delete(`${API}/admin/content/${encodeURIComponent(cfg.key)}`));
    setDraft(null);
    reload();
  };

  return (
    <div data-testid={`list-editor-${cfg.key}`} className="rounded-2xl border border-navy/10 bg-paper p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-navy">{cfg.label}
          {overrides[cfg.key] && <span className="ml-2 rounded-full bg-gold px-2 py-0.5 !text-[9px] font-sans normal-case tracking-normal text-navy">customised</span>}
        </p>
        <div className="flex gap-2">
          {overrides[cfg.key] && <button onClick={reset} className="text-[11px] font-semibold text-red-500 hover:underline">Reset to default</button>}
          {dirty && <button data-testid={`list-save-${cfg.key}`} onClick={save} className="rounded-full bg-electric px-4 py-1.5 text-[11px] font-semibold text-white hover:bg-navy">Save</button>}
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {items.map((item, i) => (
          <div key={i} className="rounded-xl border border-navy/10 bg-white p-3.5">
            <div className="grid gap-2 sm:grid-cols-2">
              {cfg.fields.map(([k, label, type]) => (
                <div key={k} className={type === 'lines' || label === 'Description' || label.includes('paragraph') ? 'sm:col-span-2' : ''}>
                  <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-navy/40">{label}</span>
                  {type === 'lines' ? (
                    <textarea rows={3} className={`${inputCls} resize-y text-xs`} value={(item[k] || []).join('\n')} onChange={(e) => update(i, k, e.target.value.split('\n').filter(Boolean))} />
                  ) : label === 'Description' || label.includes('paragraph') ? (
                    <textarea rows={2} className={`${inputCls} resize-y text-xs`} value={item[k] || ''} onChange={(e) => update(i, k, e.target.value)} />
                  ) : (
                    <input className={`${inputCls} !py-2 text-xs`} value={item[k] || ''} onChange={(e) => update(i, k, e.target.value)} />
                  )}
                </div>
              ))}
            </div>
            <button onClick={() => remove(i)} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-red-500 hover:underline"><Trash2 className="h-3 w-3" /> Remove</button>
          </div>
        ))}
      </div>
      <button onClick={add} className="mt-3 flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy hover:border-electric hover:text-electric"><Plus className="h-3.5 w-3.5" /> Add item</button>
    </div>
  );
}

function safeParse(raw, fallback) {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : fallback; } catch { return fallback; }
}

function SortableRow({ s, setEditing, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id });
  const style = { transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 30 : undefined, position: 'relative' };
  return (
    <div ref={setNodeRef} style={style} className={`flex flex-wrap items-center gap-3 rounded-2xl border bg-paper p-4 transition-shadow ${isDragging ? 'border-electric shadow-xl' : 'border-navy/10'}`}>
      <button data-testid={`section-drag-${s.id.slice(0, 8)}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-target touch-none rounded-lg text-navy/35 transition-colors hover:bg-white hover:text-electric active:cursor-grabbing">
        <GripVertical className="h-5 w-5" />
      </button>
      <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${s.theme === 'dark' ? 'bg-navy text-gold' : s.theme === 'gold' ? 'bg-gold text-navy' : 'bg-electric/10 text-electric'}`}>{s.theme}</span>
      <span className="rounded-full bg-navy/10 px-3 py-1 text-[11px] font-semibold text-navy/60">{{afterHero: 'After hero', midPage: 'Mid-page', beforeFooter: 'Before footer'}[s.placement || 'beforeFooter']}</span>
      <p className="min-w-0 flex-1 truncate text-sm font-semibold text-navy">{s.heading}</p>
      <button data-testid={`section-edit-${s.id.slice(0, 8)}`} onClick={() => setEditing({ ...s, bullets: (s.bullets || []).join('\n') })} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-4 py-2 text-xs font-semibold text-navy hover:border-electric hover:text-electric"><Pencil className="h-3.5 w-3.5" /> Edit</button>
      <button data-testid={`section-delete-${s.id.slice(0, 8)}`} onClick={onDelete} className="rounded-full border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  );
}

/* ---------------- Main section ---------------- */
const EMPTY_SECTION = { page: 'home', tag: '', heading: '', body: '', bullets: [], image: '', ctaLabel: '', ctaUrl: '', theme: 'light', placement: 'beforeFooter', order: 0 };

export function SectionsSection({ adminKey, onAuthError }) {
  const [page, setPage] = useState('home');
  const [sections, setSections] = useState([]);
  const [editing, setEditing] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [saving, setSaving] = useState(false);
  const run = useAdminFetch(adminKey, onAuthError);

  const load = useCallback(() => {
    run((c) => c.get(`${API}/admin/sections`)).then((r) => setSections(r.data)).catch(() => {});
    run((c) => c.get(`${API}/admin/content`)).then((r) => {
      const map = {};
      r.data.forEach((d) => { map[d.key] = d.value; });
      setOverrides(map);
    }).catch(() => {});
  }, [run]);
  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setEditing((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, bullets: typeof editing.bullets === 'string' ? editing.bullets.split('\n').filter(Boolean) : editing.bullets, order: Number(editing.order) || 0 };
      if (editing.id) await run((c) => c.put(`${API}/admin/sections/${editing.id}`, payload));
      else await run((c) => c.post(`${API}/admin/sections`, payload));
      setEditing(null);
      load();
    } finally { setSaving(false); }
  };

  const pageSections = sections.filter((s) => s.page === page).sort((a, b) => a.order - b.order);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = pageSections.map((s) => s.id);
    const next = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    setSections((all) => {
      const others = all.filter((s) => s.page !== page);
      const reordered = next.map((id, i) => ({ ...all.find((s) => s.id === id), order: i }));
      return [...others, ...reordered];
    });
    await run((c) => c.put(`${API}/admin/sections/reorder`, { ids: next }));
    load();
  };

  return (
    <div data-testid="admin-sections-section">
      <div className="mb-6">
        <h2 className="text-h3 font-semibold text-navy">Page Sections</h2>
        <p className="mt-1 text-sm text-navy/55">Add new sections (services, features, announcements) to any page — and edit existing mid-page content.</p>
      </div>

      {/* Add custom section */}
      <div className="rounded-3xl border border-navy/10 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-navy">Custom sections on</h3>
          <div className="flex items-center gap-2">
            <select data-testid="section-page-select" className={`${inputCls} !w-auto`} value={page} onChange={(e) => setPage(e.target.value)}>
              {PAGE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button data-testid="section-new-button" onClick={() => setEditing({ ...EMPTY_SECTION, page })} className="flex items-center gap-1.5 rounded-full bg-electric px-5 py-2.5 text-xs font-semibold text-white hover:bg-navy"><Plus className="h-3.5 w-3.5" /> New Section</button>
          </div>
        </div>

        {editing && (
          <div data-testid="section-editor" className="mt-5 grid gap-3 rounded-2xl border border-navy/10 bg-paper p-5 sm:grid-cols-2">
            <div><span className={labelCls}>Overline tag (optional)</span><input className={inputCls} value={editing.tag} onChange={set('tag')} placeholder="New · Services" /></div>
            <div><span className={labelCls}>Heading</span><input data-testid="section-heading-input" className={inputCls} value={editing.heading} onChange={set('heading')} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Body text</span><textarea rows={3} className={`${inputCls} resize-y`} value={editing.body} onChange={set('body')} /></div>
            <div className="sm:col-span-2"><span className={labelCls}>Bullet chips (one per line, optional)</span><textarea rows={2} className={`${inputCls} resize-y`} value={typeof editing.bullets === 'string' ? editing.bullets : (editing.bullets || []).join('\n')} onChange={set('bullets')} /></div>
            <div>
              <span className={labelCls}>Image (optional)</span>
              <input className={`${inputCls} font-mono`} value={editing.image || ''} onChange={set('image')} placeholder="/api/uploads/… or https://…" />
              <MediaPicker adminKey={adminKey} kinds={['image']} onPick={(url) => setEditing((f) => ({ ...f, image: url }))} testId="section-image-browse" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><span className={labelCls}>CTA label</span><input className={inputCls} value={editing.ctaLabel || ''} onChange={set('ctaLabel')} placeholder="Learn more" /></div>
              <div><span className={labelCls}>CTA link</span><input className={inputCls} value={editing.ctaUrl || ''} onChange={set('ctaUrl')} placeholder="/contact" /></div>
              <div><span className={labelCls}>Theme</span>
                <select data-testid="section-theme-select" className={inputCls} value={editing.theme} onChange={set('theme')}>
                  <option value="light">Light</option><option value="dark">Dark navy</option><option value="gold">Gold</option>
                </select>
              </div>
              <div className="col-span-2"><span className={labelCls}>Placement on page</span>
                <select data-testid="section-placement-select" className={inputCls} value={editing.placement || 'beforeFooter'} onChange={set('placement')}>
                  <option value="afterHero">Right after the hero</option>
                  <option value="midPage">Middle of the page</option>
                  <option value="beforeFooter">Before the footer (default)</option>
                </select>
              </div>
              <div><span className={labelCls}>Order (lower = higher on page)</span><input type="number" className={inputCls} value={editing.order} onChange={set('order')} /></div>
            </div>
            <div className="flex gap-2 sm:col-span-2">
              <button onClick={() => setEditing(null)} className="flex items-center gap-1.5 rounded-full border border-navy/15 px-5 py-2.5 text-xs font-semibold text-navy"><X className="h-3.5 w-3.5" /> Cancel</button>
              <button data-testid="section-save-button" onClick={save} disabled={saving || !editing.heading} className="rounded-full bg-electric px-6 py-2.5 text-xs font-semibold text-white hover:bg-navy disabled:opacity-50">{saving ? 'Saving…' : 'Save Section'}</button>
            </div>
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={pageSections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="mt-4 space-y-2" data-testid="sections-sortable-list">
              {pageSections.map((s) => (
                <SortableRow key={s.id} s={s} setEditing={setEditing} onDelete={async () => { if (window.confirm('Delete this section?')) { await run((c) => c.delete(`${API}/admin/sections/${s.id}`)); load(); } }} />
              ))}
              {pageSections.length === 0 && !editing && <p className="py-4 text-sm text-navy/45">No custom sections on this page yet.</p>}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Existing mid-page content editors */}
      <div className="mt-8 space-y-6">
        {LIST_GROUPS.map((g) => (
          <div key={g.page} className="rounded-3xl border border-navy/10 bg-white p-6">
            <h3 className="text-base font-semibold text-navy">{g.page} — existing content</h3>
            <div className="mt-4 space-y-4">
              {g.lists.map((cfg) => <ListEditor key={cfg.key} cfg={cfg} overrides={overrides} run={run} reload={load} />)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
